import { type BrowserStorage, type StoragePort } from './types';

const LEGACY_SIDEBAR_PREFIX = 'sidebar:category:';

/** Tours concluídos ficavam no nome cru do tipo antes do prefixo `tour:`. */
const LEGACY_TOUR_TYPES = [
	'company-rating',
	'contract-launch-evolution',
	'contract-history-guide',
	'edit-ongoing-contracts',
	'new-contract-history-tab',
	'tour-dailies-calendar',
	'tour-dailies-calendar-entrypoint',
	'tour-claim-simplified-flow',
	'tour-claim-creation',
	'tour-claim-stepper-guide',
	'tour-infraction-creation',
	'tour-infraction-stepper-guide',
];

/**
 * Chaves que deixaram de existir e não valem a migração: estado de listagem
 * de contratos, e caches por transportadora que o escopo `company` refaz.
 */
const LEGACY_ORPHANS = [
	'ecommerce-tab:vehicle-collapse',
	'ongoing-dailies:filters',
	'ongoing-tab:status-collapse',
	'hiring-tab:status-collapse',
	'pre-shipment-tab:status-collapse',
	'pre-shipment-tab:date-collapse',
	'notification-preview',
	'last-notification',
	'company-watcher,retry-date',
	'reset-password,retry-date',
	...LEGACY_TOUR_TYPES,
	...[
		'on-revision-tab',
		'pre-shipment-tab',
		'waiting-gr-tab',
		'hiring-tab',
		'ongoing-tab',
		'finished-tab',
		'canceled-tab',
		'ecommerce-tab',
	].flatMap((tab) => [
		`${tab}:operation-collapse`,
		`${tab}:group-by`,
		`${tab}:filters`,
	]),
];

/** Famílias de chaves por tipo ou por transportadora, apagadas por prefixo. */
const LEGACY_ORPHAN_PREFIXES = [
	'panel_jwt:',
	'panel_jwt_expires_at:',
	'tour:',
	'px-protect-plan-id-',
	'welcome:skippedAt:',
	'financial_report_first_view_',
];

const parse = (raw: string | null): unknown => {
	if (raw === null) return undefined;
	try {
		return JSON.parse(raw);
	} catch {
		return undefined;
	}
};

/**
 * Converte o que existia antes do Browser Storage para Chaves Registradas e
 * apaga o legado. Idempotente: cada passo só roda enquanto a chave antiga
 * existir, então rodar a cada boot custa algumas leituras.
 */
export const migrateLegacy = (storage: BrowserStorage, local: StoragePort) => {
	const hiddenBalance = local.getItem('preferences:hiddenBalance');
	if (hiddenBalance !== null) {
		storage.set('hiddenBalance', hiddenBalance === 'true');
		local.removeItem('preferences:hiddenBalance');
	}

	const statementHidden = parse(local.getItem('statement:hiddenValues'));
	if (statementHidden !== undefined) {
		storage.set('statementHiddenValues', statementHidden === true);
		local.removeItem('statement:hiddenValues');
	}

	const sidebarNames = local
		.names()
		.filter((name) => name.startsWith(LEGACY_SIDEBAR_PREFIX));
	if (sidebarNames.length) {
		storage.set('sidebarCategoryCollapse', (prev) => {
			const next = { ...prev };
			for (const name of sidebarNames) {
				next[name.slice(LEGACY_SIDEBAR_PREFIX.length)] =
					local.getItem(name) !== 'false';
			}
			return next;
		});
		sidebarNames.forEach((name) => local.removeItem(name));
	}

	const watcherSession = parse(local.getItem('company-watcher:session'));
	if (watcherSession !== undefined) {
		if (
			typeof watcherSession === 'object' &&
			watcherSession !== null &&
			'token' in watcherSession
		) {
			storage.set('companyWatcherSession', {
				token: String(watcherSession.token),
				managerName: String(
					'managerName' in watcherSession ? watcherSession.managerName : '',
				),
				isMaster:
					'isMaster' in watcherSession && watcherSession.isMaster === true,
			});
		}
		local.removeItem('company-watcher:session');
	}

	if (local.getItem('track-consent') === 'accepted') {
		storage.set('trackerConsent', true);
	}
	local.removeItem('track-consent');

	const apiOverride = local.getItem('px-painel-api-override');
	if (apiOverride) {
		storage.set('apiOverride', apiOverride);
		local.removeItem('px-painel-api-override');
	}

	const fcmToken = local.getItem('fcm-token');
	if (fcmToken) {
		storage.set('fcmToken', fcmToken);
		local.removeItem('fcm-token');
	}

	if (local.getItem('force-pwa') === '1') storage.set('forcePwa', true);
	local.removeItem('force-pwa');

	if (local.getItem('pwa-installed-tracked') !== null) {
		storage.set('pwaInstalledTracked', true);
		local.removeItem('pwa-installed-tracked');
	}

	LEGACY_ORPHANS.forEach((name) => local.removeItem(name));
	local
		.names()
		.filter((name) =>
			LEGACY_ORPHAN_PREFIXES.some((prefix) => name.startsWith(prefix)),
		)
		.forEach((name) => local.removeItem(name));
};
